import { useAuthState } from '../../services/auth';
import { useEffect, useMemo, useState } from 'react';
import '../../design-system/index.css';
import './operator-summary.scss';
import axios from 'axios';
import { TableWidget } from '../../components/table-widget/TableWidget';
import { useNavigate, useParams } from 'react-router-dom';
import { useOperatorsState } from '../../services/OperatorsList';
import React from 'react';
import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import GaugeComponent from 'react-gauge-component';
import { useSocket } from '../../hooks/useSocket';
import CountUp from 'react-countup';
import { ArrowRight, Globe, LucideIcon, MonitorSmartphone, Router, ShieldAlert } from 'lucide-react';
import { CpeCardsView } from '../../components/Registered Accounts/CpeCardsView';
import { fonts } from '../../design-system/tokens';
import type { BDTheme } from '../../design-system/types';
import { chartTokens } from '../../design-system/chartTokens';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner } from '../../components/loading-spinner/LoadingSpinner';

// Brighter chart/gauge text in the dark theme (matches the page's text-token overrides)
const pageChartTokens = (theme: BDTheme) =>
  theme === 'dark'
    ? { ...chartTokens.dark, text: '#ffffff', text2: '#e4eff1', mute: '#c3d5d9' }
    : chartTokens.light;

// Deviation within ±33.33% is considered healthy (middle arc of the gauge).
const isHealthy = (value: number) => value >= -33.33 && value <= 33.33;

type HealthCardProps = {
  theme: BDTheme;
  label: string;
  icon: LucideIcon;
  count: number;
  percentage: number;
  href: string;
  onNavigate: () => void;
};

const HealthCard = ({ theme, label, icon: Icon, count, percentage, href, onNavigate }: HealthCardProps) => {
  // `t` here holds chart tokens, so the translate function is aliased
  const { t: translate } = useTranslation();
  const t = pageChartTokens(theme);
  const healthy = isHealthy(percentage);
  const tone = healthy ? 'ok' : 'alert';

  return (
    <div className={`bd-card os-kpi ${tone}`}>
      <div className="os-kpi-hd">
        <span className="os-kpi-icon">
          <Icon size={18} strokeWidth={1.75} />
        </span>
        <span className="os-kpi-label">{label}</span>
        <span className={`bd-chip ${healthy ? 'mint' : 'danger'}`}>
          <span className="dot" />
          {healthy ? translate('summary.kpi.healthy') : translate('summary.kpi.attention')}
        </span>
      </div>

      <div className="os-kpi-body">
        <CountUp className="bd-stat-num" preserveValue={true} end={count ?? 0} duration={2} separator="," />

        <div className="os-gauge">
          <GaugeComponent
            value={percentage}
            type="radial"
            minValue={-100}
            maxValue={100}
            marginInPercent={{ top: 0.06, bottom: 0, left: 0.07, right: 0.07 }}
            labels={{
              tickLabels: {
                type: 'outer',
                ticks: [{ value: -100 }, { value: -33 }, { value: 33 }, { value: 100 }],
                defaultTickValueConfig: { style: { fill: t.mute, fontSize: '11px', fontFamily: fonts.mono } },
                defaultTickLineConfig: { color: t.border, length: 4 },
              },
              valueLabel: { matchColorWithArc: false, hide: true },
            }}
            arc={{
              colorArray: [
                percentage < -33.33 ? t.danger : t.track,
                healthy ? t.accent : t.track,
                percentage > 33.33 ? t.danger : t.track,
              ],
              subArcs: [{ limit: -33.33 }, { limit: 33.33 }, {}],
              padding: 0.02,
              width: 0.22,
            }}
            pointer={{
              elastic: true,
              type: 'needle',
              color: t.text2,
              baseColor: t.text2,
              animationDelay: 500,
              length: 0.7,
              width: 12,
            }}
          />
        </div>
      </div>

      <div className="os-kpi-ft">
        <span className="bd-meta">
          <span className="bd-mono os-kpi-delta">
            {percentage > 0 ? '+' : ''}
            {percentage}%
          </span>{' '}
          {translate('summary.kpi.deviation')}
        </span>
        <a
          href={href}
          className={`bd-pill os-kpi-action ${healthy ? 'accent' : 'danger'}`}
          onClick={(e) => {
            e.preventDefault();
            onNavigate();
          }}
        >
          {healthy ? translate('summary.kpi.seeDetails') : translate('summary.kpi.investigate')}
          <ArrowRight size={13} />
        </a>
      </div>
    </div>
  );
};

export const OperatorSummary = (props: any) => {
  const { t } = useTranslation();
  const { operatorId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuthState();
  const theme: BDTheme = props.darkMode ? 'dark' : 'light';
  const [showAccount, setShowAccount] = useState(false);
  /*const [devicesManaged, setDevicesManaged] = useState<any>();
  const [mobileWithApp, setMobileWithApp] = useState<any>();
  const [threatsMitigated, ] = useState<any>();
  */
  const AccessDenied = () => (
    <div className="flex justify-center items-center text-textMuted text-xl font-medium mt-10" style={{ height: '80vh' }}>
      <div className="text-center">{t('summary.accessDenied')}</div>
    </div>
  );
  window.localStorage.setItem('currentNav', 'Home');
  const [routerClicked, setRouterClicked] = useState<boolean>(false);
  const [cpeData, setCpeData] = useState<number>(0);
  const [percentageData, setPercentageData] = useState<any>();
  const [cpeDataPercentage, setCpeDataPercentage] = useState<number>(0);
  const [devicesDataPercentage, setDevicesDataPercentage] = useState<number>(0);
  const [dnsDataPercentage, setDnsDataPercentage] = useState<number>(0);
  const [threatDataPercentage, setThreatDataPercentage] = useState<number>(0);
  const [devicesData, setDevicesData] = useState<number>(0);
  const [threatData, setThreatData] = useState<number>(0);
  const [dnsData, setDnsData] = useState<number>(0);
  const [verifiedCount, setVerifiedCount] = useState<number>(0);
  const [unverifiedCount, setUnverifiedCount] = useState<number>(0);
  const {
    deviceAmount,
    routerAmount,
    todayDnsRecordsAmount,
    todayThreatsAmount,
  } = useSocket();

  const fetchPercentageData = async () => {
    try {
      const response = await axios.get(`/v2/op/master-operator/${operatorId}/dashboard-percentages`);
      const data = response.data;

      const percentageData = {
        cpes: {
          level: data?.cpes?.level || 'No Data',
          count: data?.cpes?.count || '0%'
        },
        devices: {
          level: data?.devices?.level || 'No Data',
          count: data?.devices?.count || '0%'
        },
        dns: {
          level: data?.dns?.level || 'No Data',
          count: data?.dns?.count || '0%'
        },
        threats: {
          level: data?.threats?.level || 'No Data',
          count: data?.threats?.count || '0%'
        }
      };

      // Example: Store in state (React)
      setPercentageData(percentageData);
      setCpeDataPercentage(parseFloat(percentageData.cpes.count.replace('%', '')) || 0);
      setDevicesDataPercentage(parseFloat(percentageData.devices.count.replace('%', '')) || 0);
      setDnsDataPercentage(parseFloat(percentageData.dns.count.replace('%', '')) || 0);
      setThreatDataPercentage(parseFloat(percentageData.threats.count.replace('%', '')) || 0);
    } catch (error) {
      console.error('Error fetching widget data:', error);
    }
  };

  useEffect(() => {
    // fetchWidgetData();
    fetchPercentageData();
  }, []);
  const fakeRouterData = [
    {
      VulnId: 'CVE-2024-20481',
      AffectedServices: 'Cisco ASA and FTD',
      PublishedDate: 'Oct 31, 2024',
      CVSSVersion: 'V3.1',
      Severity: '5.8 MEDIUM',
      url: 'https://nvd.nist.gov/vuln/detail/CVE-2024-20481',
    },
    {
      VulnId: 'CVE-2024-47575',
      AffectedServices: 'FortiManager',
      PublishedDate: 'Oct 31, 2024',
      CVSSVersion: 'V3.1',
      Severity: '9.8 CRITICAL',
      url: 'https://nvd.nist.gov/vuln/detail/CVE-2024-47575',
    },
    {
      VulnId: 'CVE-2024-9537',
      AffectedServices: 'ScienceLogic SL1',
      PublishedDate: 'Oct 31, 2024',
      CVSSVersion: 'V3.1',
      Severity: '9.8 CRITICAL',
      url: 'https://nvd.nist.gov/vuln/detail/CVE-2024-9537',
    },
  ];

  const handleRowClick = (url: string) => {
    console.log('Redirecting to', url);
    window.location.href = url;
    setRouterClicked(true);
  };

  const [summaryData, setSummaryData] = useState<any>();
  //const [loggedUserData, setLoggedUserData] = useState<any>()
  const { operatorList, loggedUserData } = useOperatorsState();
  const [interests, setInterests] = useState<any>();
  const [customerId, setCustomerId] = useState('');
  const [isClicked, setIsClicked] = useState(false);
  const [cveData, setCveData] = useState<any>();
  const toggleAccount = () => {
    setShowAccount(!showAccount);
  };

  useEffect(() => {
    if (token) {
      fetchApi();
      fetchInterests();
    }
  }, [token]);

  // loggedUserData loads asynchronously after login, so re-sync when it arrives
  useEffect(() => {
    tokenCheck();
  }, [token, loggedUserData?.operatorId]);
  async function tokenCheck() {
    if (token) {
      setCustomerId(loggedUserData?.operatorId);
    }
  }
  async function fetchInterests() {
    const resp = await axios.get(`op/${operatorId}/interests`);
    setInterests(resp?.data?.data?.interests);
  }

  // /op/operatordevices/0?page=1&size=999999999999999999999&l=

  async function fetchApi() {
    const data = await axios.get('/op/' + operatorId);
    setSummaryData(data.data);
    deviceDistributionData(data.data.devices_by_type);
  }

  function goToCpePage(rowData: any) {
    navigate(`/operators/${operatorId}/cpe/${rowData.key.ID}`);
  }

  function cvePopup(rowData: any) {
    console.log('rowDatad', rowData);
    localStorage.setItem("cveData", JSON.stringify(rowData.key["deviceIds"]));
    navigate(`/operators/${operatorId}/cve/${rowData.key['Vuln. Id']}`);

  }

  const [deviceTypes, setDeviceTypes] = useState<{ categories: string[]; counts: number[] }>({ categories: [], counts: [] });

  function deviceDistributionData(data: any) {
    const devicesByType = data || [];

    setDeviceTypes({
      categories: devicesByType.map((device: { type: any }) => device.type),
      counts: devicesByType.map((device: { count: any }) => device.count),
    });
  }

  const devicesSeriesName = t('summary.deviceDistribution.seriesName');
  const deviceBarChart = useMemo(() => {
    const t = pageChartTokens(theme);
    return {
      series: [{ name: devicesSeriesName, data: deviceTypes.counts }],
      options: {
        chart: {
          height: 260,
          type: 'bar',
          background: 'transparent',
          fontFamily: fonts.sans,
          foreColor: t.mute,
          toolbar: { show: false },
        },
        theme: { mode: theme },
        plotOptions: {
          bar: {
            borderRadius: 6,
            borderRadiusApplication: 'end',
            columnWidth: '45%',
            dataLabels: { position: 'top' },
          },
        },
        colors: [t.accent],
        dataLabels: {
          enabled: true,
          offsetY: -20,
          style: { fontSize: '13px', fontFamily: fonts.mono, fontWeight: 500, colors: [t.text2] },
        },
        grid: {
          borderColor: t.border,
          strokeDashArray: 3,
          xaxis: { lines: { show: false } },
        },
        states: { hover: { filter: { type: 'lighten', value: 0.08 } } },
        tooltip: { theme },
        xaxis: {
          categories: deviceTypes.categories,
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: t.mute, fontSize: '13px' } },
          crosshairs: { fill: { type: 'solid', color: t.track } },
          tooltip: { enabled: false },
        },
        yaxis: {
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { show: true, style: { colors: t.mute, fontFamily: fonts.mono } },
        },
      } as ApexOptions,
    };
  }, [deviceTypes, theme, devicesSeriesName]);

  const changeRoute = (_linkUrl: any, _linkedpage: any) => {
    navigate(_linkUrl, { state: { key: 'Threats 24hrs' } });
    window.localStorage.setItem('currentNav', _linkedpage);
  };

  const healthCards = [
    {
      label: t('summary.kpi.cpesManaged'),
      icon: Router,
      count: routerAmount,
      percentage: cpeDataPercentage,
      href: `/general-list/${loggedUserData?.operatorId}/operatorcpe/Total Accounts Managed`,
      page: 'Accounts',
    },
    {
      label: t('summary.kpi.devicesManaged'),
      icon: MonitorSmartphone,
      count: deviceAmount,
      percentage: devicesDataPercentage,
      href: `/general-list/${loggedUserData?.operatorId}/operatordevices/Total Devices Managed`,
      page: 'Devices',
    },
    {
      label: t('summary.kpi.todaysDnsRequests'),
      icon: Globe,
      count: todayDnsRecordsAmount,
      percentage: dnsDataPercentage,
      href: `/dnsactivity/${loggedUserData?.operatorId}/`,
      page: 'Activity',
    },
    {
      label: t('summary.kpi.todaysThreats'),
      icon: ShieldAlert,
      count: todayThreatsAmount,
      percentage: threatDataPercentage,
      href: `/threatsmanaged/${loggedUserData?.operatorId}/`,
      page: 'Threats',
    },
  ];
  const attentionCount = healthCards.filter((card) => !isHealthy(card.percentage)).length;

  return (
    <div className="bd os-page" data-theme={theme} data-accent="mint">
      <section id="operator-summary">
        {/* <RetinaHeader title="BlackDice Cyber - Operator Summary" /> */}
        <div className="flex flex-col w-full ml-auto mr-auto">
          <header className="os-header">
            <div className="os-header-text">
              <span className="os-eyebrow">{t('summary.header.eyebrow')}</span>
              <h1 className="bd-h1 os-title">{t('summary.header.title')}</h1>
              <span className="bd-body">{t('summary.header.subtitle')}</span>
            </div>
            <span className="bd-chip mint os-live">
              <span className="dot" />
              {t('summary.header.live')}
            </span>
          </header>

          {(customerId == operatorId || customerId == '0') && !showAccount ? (
            <div className="os-body">
              <div className="os-section">
                <div className="os-section-hd">
                  <h3>{t('summary.health.title')}</h3>
                  <span className={`bd-chip ${attentionCount ? 'danger' : 'mint'}`}>
                    {attentionCount ? t('summary.health.needAttention', { count: attentionCount }) : t('summary.health.allNominal')}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {healthCards.map((card) => (
                    <HealthCard
                      key={card.label}
                      theme={theme}
                      label={card.label}
                      icon={card.icon}
                      count={card.count}
                      percentage={card.percentage}
                      href={card.href}
                      onNavigate={() => changeRoute(card.href, card.page)}
                    />
                  ))}
                </div>
              </div>

              {/* <div className="w-[100%] rounded-2xl shadow-md ">
                <TableWidget
                  hasSearch={true}
                  url={`op/${operatorId}/cpe_table`}
                  onRowSelect={goToCpePage}
                  countPerPage={2}
                  title="Registered Accounts"
                  subTitle="Details of registered accounts with router details"
                  searchPositionRight={true}
                />
              </div> */}
              <CpeCardsView
                operatorId={loggedUserData?.operatorId}
                darkMode={props.darkMode}
              />

              <div className="os-bottom">
                <div className="bd-card os-table-card">
                  <TableWidget
                    hasSearch={false}
                    url={`v2/op/device-cves/most-resent-cves/${operatorId}`}
                    onRowSelect={cvePopup}
                    countPerPage={5}
                    title={t('summary.cveTable.title')}
                    subTitle={t('summary.cveTable.subtitle')}
                    searchPositionRight={true}
                  />
                </div>

                <div className="bd-card bd-card-pad os-chart-card">
                  <div className="os-card-hd">
                    <h2 className="bd-h3">{t('summary.deviceDistribution.title')}</h2>
                    <span className="bd-meta">{t('summary.deviceDistribution.subtitle')}</span>
                  </div>
                  <ReactApexChart
                    key={theme}
                    options={deviceBarChart.options}
                    series={deviceBarChart.series}
                    type="bar"
                    height={260}
                  />
                </div>
              </div>
            </div>
          ) : customerId !== '' && customerId != null && !(customerId == operatorId || customerId == '0') ? (
            // operator data has loaded and this user may not view this operator
            <AccessDenied />
          ) : (
            <LoadingSpinner />
          )}
          {isClicked && (
            <div className="flex items-center justify-center w-full h-[93.9vh] absolute bg-black bg-opacity-50">
              <div className="flex-col bg-white shadow-md p-5 rounded-xl shadow-2xl" style={{ width: '970px' }}>
                <h3>{t('summary.cveModal.title', { id: cveData.cveId })}</h3>
                <div className="mb-3 flex justify-between">
                  <div className="mr-10">
                    <h5>{t('summary.cveModal.description')}</h5>
                  </div>
                  <div>
                    <p>{cveData.description}</p>
                  </div>
                </div>
                <div className="flex mb-3 items-center">
                  <div className="flex mr-10">
                    <p>{cveData.cvssVersion}</p>
                    <span>3.1</span>
                  </div>
                  <div className="flex mr-10 items-center">
                    <h5 className="mr-5">{t('summary.cveModal.nistBaseScore')}</h5>
                    <span className="bg-black text-white pl-3 pr-3 h-10 flex items-center">{cveData.nistBaseScore}</span>
                  </div>
                  <div className="flex mr-10 items-center">
                    <h5 className="mr-5">{t('summary.cveModal.cisaBaseScore')}</h5>
                    <span className="bg-black text-white pl-3 pr-3 h-10 flex items-center">{cveData.cisaBaseScore}</span>
                  </div>
                </div>
                <div className="mt-3 flex">
                  <div className="mr-10">
                    <h5>{t('summary.cveModal.cweLink')}</h5>
                  </div>
                  <div>
                    <a href={`https://nvd.nist.gov/vuln/detail/${cveData.cveId}`} target="_blank">
                      {cveData.cveId}
                    </a>
                  </div>
                </div>
                <div className="retina-table  rounded-xl w-[60%] shadow-md h-full"  >
                  {/* <TableWidget
                      hasSearch={false}
                      url={`v2/op/device/list-devices-by-deviceIds?page=1&size=5`}
                      onRowSelect={cvePopup}
                      countPerPage={3}
                      title="New CVE Threats"
                      subTitle="Details of recently Published CVE Threats"
                      searchPositionRight={true}
                      deviceIds= {cveData?.deviceIds}
                    /> */}

                  {cveData?.deviceIds}
                </div>
                <div className="mt-3">
                  <div className="flex flex-col justify-center  items-center  ">
                    <button className="scan-btn" onClick={() => setIsClicked(false)}>
                      {t('summary.cveModal.close')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};
